import React, { useState, useEffect } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";

const PremiumInvoiceTable = ({ initialItems = [], onItemsChange, taxRate = 0, discount = 0 }) => {
    const [items, setItems] = useState(
        initialItems.length > 0 ? initialItems : [{ item: "", qty: 1, unitPrice: 0, amount: 0 }]
    );

    const calculateTotals = (currentItems) => {
        const subTotal = currentItems.reduce((sum, item) => sum + (item.amount || 0), 0);
        const taxAmount = subTotal * (taxRate / 100);
        const totalAmount = subTotal + taxAmount - discount;
        return { subTotal, taxAmount, totalAmount };
    };

    useEffect(() => {
        if (initialItems.length > 0) setItems(initialItems);
    }, [initialItems]);

    useEffect(() => {
        const totals = calculateTotals(items);
        onItemsChange(items, totals);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const totals = calculateTotals(items);
        onItemsChange(items, totals);
    }, [taxRate, discount]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (index, field, value) => {
        const newItems = [...items];
        const item = { ...newItems[index] };

        if (field === "qty" || field === "unitPrice") {
            const val = parseFloat(value) || 0;
            item[field] = val;
            item.amount = (field === "qty" ? val : item.qty) * (field === "unitPrice" ? val : item.unitPrice);
        } else {
            item[field] = value;
        }

        newItems[index] = item;
        setItems(newItems);

        const totals = calculateTotals(newItems);
        onItemsChange(newItems, totals);
    };

    const addItem = () => {
        const newItems = [...items, { item: "", qty: 1, unitPrice: 0, amount: 0 }];
        setItems(newItems);
    };

    const removeItem = (index) => {
        if (items.length <= 1) return;
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
        const totals = calculateTotals(newItems);
        onItemsChange(newItems, totals);
    };

    const totals = calculateTotals(items);

    const isRowIncomplete = (row) => {
        return !row.item.trim() || row.qty <= 0 || row.unitPrice <= 0;
    };

    return (
        <div className="w-full space-y-6">
            <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-xl bg-white/50 backdrop-blur-sm">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-900 text-white">
                            <th className="px-4 py-4 text-xs font-black uppercase tracking-widest w-10 text-center">#</th>
                            <th className="px-4 py-4 text-xs font-black uppercase tracking-widest">Description</th>
                            <th className="px-4 py-4 text-xs font-black uppercase tracking-widest w-28">Qty</th>
                            <th className="px-4 py-4 text-xs font-black uppercase tracking-widest w-40">Unit Price</th>
                            <th className="px-4 py-4 text-xs font-black uppercase tracking-widest w-40 text-right">Amount</th>
                            <th className="px-4 py-4 w-14"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {items.map((row, idx) => (
                            <tr
                                key={idx}
                                className={`group transition-colors ${
                                    isRowIncomplete(row) && row.item.trim() !== ""
                                        ? "bg-amber-50/50 hover:bg-amber-50"
                                        : "hover:bg-slate-50"
                                }`}
                            >
                                <td className="px-4 py-4 text-center">
                                    <span className="text-xs font-bold text-slate-300">{idx + 1}</span>
                                </td>
                                <td className="px-4 py-4">
                                    <input
                                        type="text"
                                        placeholder="Enter item or service description..."
                                        className={`w-full bg-transparent border-none focus:ring-0 font-medium text-slate-700 placeholder:text-slate-300 ${
                                            !row.item.trim() ? "placeholder:text-amber-300" : ""
                                        }`}
                                        value={row.item}
                                        onChange={(e) => handleChange(idx, "item", e.target.value)}
                                    />
                                </td>
                                <td className="px-4 py-4">
                                    <input
                                        type="number"
                                        min="1"
                                        placeholder="1"
                                        className="w-full bg-slate-50 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent px-3 py-2 font-bold text-slate-700 text-center"
                                        value={row.qty || ""}
                                        onChange={(e) => handleChange(idx, "qty", e.target.value)}
                                    />
                                </td>
                                <td className="px-4 py-4">
                                    <div className="flex items-center gap-1 bg-slate-50 rounded-xl border border-slate-200 px-3 py-2">
                                        <span className="text-xs font-bold text-slate-400">Nu.</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            placeholder="0.00"
                                            className="w-full bg-transparent border-none focus:ring-0 font-bold text-slate-700 p-0"
                                            value={row.unitPrice || ""}
                                            onChange={(e) => handleChange(idx, "unitPrice", e.target.value)}
                                        />
                                    </div>
                                </td>
                                <td className="px-4 py-4 text-right">
                                    <span className={`font-black ${row.amount > 0 ? "text-slate-900" : "text-slate-300"}`}>
                                        Nu. {(row.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </span>
                                </td>
                                <td className="px-4 py-4 text-center">
                                    {items.length > 1 ? (
                                        <button
                                            onClick={() => removeItem(idx)}
                                            className="opacity-0 group-hover:opacity-100 p-2 text-slate-300 hover:text-rose-500 transition-all hover:bg-rose-50 rounded-xl"
                                            title="Remove item"
                                        >
                                            <FaTrash size={12} />
                                        </button>
                                    ) : (
                                        <span className="p-2 inline-block w-7" />
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="p-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between">
                    <button
                        onClick={addItem}
                        className="flex items-center gap-2 px-6 py-2.5 bg-white text-indigo-600 rounded-xl border border-indigo-100 shadow-sm hover:bg-indigo-600 hover:text-white transition-all font-bold text-sm"
                    >
                        <FaPlus className="text-xs" /> Add Item
                    </button>
                    <span className="text-xs text-slate-400 font-medium">
                        {items.filter((i) => i.item.trim() !== "").length} of {items.length} items filled
                    </span>
                </div>
            </div>

            <div className="flex flex-col md:flex-row justify-between gap-10">
                <div className="flex-1 space-y-4 max-w-md">
                    <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-4">
                        <h4 className="text-xs font-black uppercase text-slate-400 tracking-widest flex items-center gap-2">
                            Notes & Payment Terms
                        </h4>
                        <textarea
                            placeholder="Additional details, bank info, or terms..."
                            className="w-full h-32 bg-slate-50 border-none rounded-2xl p-4 focus:ring-2 focus:ring-indigo-500 text-sm font-medium text-slate-600"
                        ></textarea>
                    </div>
                </div>

                <div className="w-full md:w-80 space-y-3">
                    <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-2xl space-y-4">
                        <div className="flex justify-between items-center text-slate-500 font-medium">
                            <span className="text-sm">Subtotal</span>
                            <span className="font-bold">Nu. {totals.subTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between items-center text-slate-500 font-medium">
                            <span className="text-sm">Tax ({taxRate}%)</span>
                            <span className="font-bold">Nu. {totals.taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between items-center text-rose-500 font-medium pb-4 border-b border-slate-50">
                            <span className="text-sm">Discount</span>
                            <span className="font-bold">- Nu. {discount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between items-center text-slate-900 py-2">
                            <span className="text-lg font-black tracking-tight">Total Amount</span>
                            <span className="text-2xl font-black text-indigo-600">
                                Nu. {totals.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PremiumInvoiceTable;
